import { useEffect, useState, type ReactNode } from 'react';
import { useT } from '@/shared/i18n';
import { usePrefersReducedMotion } from '@/shared/hooks/usePrefersReducedMotion';
import { cn } from '@/shared/lib/cn';
import { Icon } from '@/shared/ui/icon';

/**
 * ScrollToTopButton — a floating action button that fades in once the user has
 * scrolled past the fold and smooth-scrolls back to the top on click. Pinned to
 * the bottom trailing corner (mirrors under RTL). Smooth scroll is gated by
 * `prefers-reduced-motion`. Theme-adaptive via tokens.
 */
export function ScrollToTopButton(): ReactNode {
  const { t } = useT();
  const reduced = usePrefersReducedMotion();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = (): void => setVisible(window.scrollY > 480);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toTop = (): void => {
    window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
  };

  return (
    <button
      type="button"
      onClick={toTop}
      aria-label={t('landing.scrollTop.label')}
      title={t('landing.scrollTop.label')}
      data-testid="scroll-to-top"
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      className={cn(
        // Sits above the global AI-assistant FAB (which owns bottom-6) so they stack.
        'fixed bottom-[5.75rem] right-6 z-50 flex h-12 w-12 items-center justify-center rounded-full border border-outline/15 bg-surface-container-lowest text-on-surface shadow-lg transition-all duration-300 ease-standard hover:scale-110 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/60 rtl:left-6 rtl:right-auto',
        visible
          ? 'translate-y-0 opacity-100'
          : 'pointer-events-none translate-y-4 opacity-0',
      )}
    >
      <Icon name="arrow-up" size={22} />
    </button>
  );
}

export default ScrollToTopButton;
